import React, { useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { generateAutoContent } from '../../utils/api';

const inputCls =
  'w-full bg-black border border-divider rounded-lg px-3 py-2 text-white text-sm focus:border-lime focus:outline-none';

export const GenerateModal = ({ clients, defaultClientId, onClose, onGenerated }) => {
  const [form, setForm] = useState({
    client_id: defaultClientId || clients[0]?.id || '',
    content_type: 'social_post',
    platform: 'facebook',
    topic: '',
    count: 1,
    extra_instructions: '',
  });
  const [loading, setLoading] = useState(false);

  const setField = (k, v) => {
    const next = { ...form, [k]: v };
    if (k === 'content_type') {
      next.platform = v === 'social_post' ? 'facebook' : 'meta_ads';
    }
    setForm(next);
  };

  const platforms = form.content_type === 'social_post'
    ? [['facebook', 'Facebook'], ['instagram', 'Instagram']]
    : [['meta_ads', 'Meta Ads'], ['google_ads', 'Google Ads']];

  const handleGenerate = async () => {
    if (!form.topic.trim()) {
      toast.error('Enter a topic or prompt');
      return;
    }
    setLoading(true);
    try {
      const res = await generateAutoContent({ ...form, count: Number(form.count) });
      toast.success(`${res.data.created.length} draft(s) added to the approval queue`);
      onGenerated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="generate-modal">
      <div className="bg-card-bg border border-divider rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-divider">
          <h3 className="text-white font-bold text-lg flex items-center gap-2">
            <Sparkles size={18} className="text-lime" /> Generate Content with AI
          </h3>
          <button onClick={onClose} data-testid="generate-modal-close" className="text-gray hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-gray uppercase font-semibold">Client</label>
            <select data-testid="generate-client-select" className={inputCls} value={form.client_id} onChange={(e) => setField('client_id', e.target.value)}>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray uppercase font-semibold">Content Type</label>
              <select data-testid="generate-type-select" className={inputCls} value={form.content_type} onChange={(e) => setField('content_type', e.target.value)}>
                <option value="social_post">Social Post</option>
                <option value="ad">Paid Ad</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray uppercase font-semibold">Platform</label>
              <select data-testid="generate-platform-select" className={inputCls} value={form.platform} onChange={(e) => setField('platform', e.target.value)}>
                {platforms.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray uppercase font-semibold">Topic / Prompt *</label>
            <textarea
              data-testid="generate-topic-input"
              className={inputCls}
              rows={3}
              value={form.topic}
              onChange={(e) => setField('topic', e.target.value)}
              placeholder="Summer AC tune-up special — book before July and save $50"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray uppercase font-semibold"># of Drafts</label>
              <select data-testid="generate-count-select" className={inputCls} value={form.count} onChange={(e) => setField('count', e.target.value)}>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray uppercase font-semibold">Extra Instructions</label>
              <input data-testid="generate-extra-input" className={inputCls} value={form.extra_instructions} onChange={(e) => setField('extra_instructions', e.target.value)} placeholder="Keep it under 100 words" />
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-divider flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-gray-light hover:text-white text-sm">Cancel</button>
          <button
            data-testid="generate-submit-btn"
            onClick={handleGenerate}
            disabled={loading}
            className="px-5 py-2 rounded-lg bg-lime text-black font-bold text-sm hover:bg-lime/90 disabled:opacity-50 flex items-center gap-2"
          >
            <Sparkles size={16} />
            {loading ? 'Drafting with AI…' : 'Generate Drafts'}
          </button>
        </div>
      </div>
    </div>
  );
};
